import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import FlowDiagram from './components/FlowDiagram';
import HungarianViewer from './components/HungarianViewer';
import PrintReport from './components/PrintReport';
import StepViewer from './components/StepViewer';
import { hungarian, HungarianResult } from './solver/hungarian';
import { minimumCost } from './solver/minCost';
import { northwestCorner } from './solver/northwest';
import { optimize } from './solver/optimize';
import { buildEffective, realObjective } from './solver/transform';
import { MethodResult, OptimizeResult, Problem } from './solver/types';
import { balanceProblem } from './solver/utils';
import {
  decodeFromHash,
  downloadJson,
  encodeToHash,
  loadLocal,
  SavedState,
  saveLocal,
} from './persist';

type Method = 'noroeste' | 'costo';
type Mode = 'transporte' | 'asignacion';

interface TransportSolution {
  kind: 'transporte';
  problem: Problem; // problema efectivo balanceado
  balanceNote: string | null;
  maximizeNote: string | null;
  method: Method;
  maximize: boolean;
  initial: MethodResult;
  optimal: OptimizeResult;
  initialObjective: number;
  optimalObjective: number;
}

interface AssignSolution {
  kind: 'asignacion';
  result: HungarianResult;
  rowLabels: string[];
  colLabels: string[];
  maximizeNote: string | null;
  objective: number;
  maximize: boolean;
}

type Solution = TransportSolution | AssignSolution;

const DEFAULT: SavedState = {
  mode: 'transporte',
  m: 3,
  n: 3,
  costs: [
    [8, 6, 10],
    [9, 12, 13],
    [14, 9, 16],
  ],
  supply: [35, 50, 40],
  demand: [45, 20, 60],
  sources: ['O1', 'O2', 'O3'],
  dests: ['D1', 'D2', 'D3'],
  forbidden: [
    [false, false, false],
    [false, false, false],
    [false, false, false],
  ],
  maximize: false,
  method: 'noroeste',
};

const App: React.FC = () => {
  const initial = decodeFromHash() ?? loadLocal() ?? DEFAULT;

  const [mode, setMode] = useState<Mode>(initial.mode);
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [m, setM] = useState(initial.m);
  const [n, setN] = useState(initial.n);
  const [costs, setCosts] = useState<number[][]>(initial.costs);
  const [supply, setSupply] = useState<number[]>(initial.supply);
  const [demand, setDemand] = useState<number[]>(initial.demand);
  const [sources, setSources] = useState<string[]>(initial.sources);
  const [dests, setDests] = useState<string[]>(initial.dests);
  const [forbidden, setForbidden] = useState<boolean[][]>(initial.forbidden);
  const [maximize, setMaximize] = useState<boolean>(initial.maximize);
  const [method, setMethod] = useState<Method>(initial.method);
  const [prohibitMode, setProhibitMode] = useState(false);

  const [solution, setSolution] = useState<Solution | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Aplica el tema al fondo de la página.
  useEffect(() => {
    document.body.style.background = theme === 'dark' ? '#0f172a' : '#f1f5f9';
  }, [theme]);

  const snapshot = (): SavedState => ({
    mode,
    m,
    n,
    costs,
    supply,
    demand,
    sources,
    dests,
    forbidden,
    maximize,
    method,
  });

  // Persistencia automática en localStorage.
  useEffect(() => {
    saveLocal(snapshot());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, m, n, costs, supply, demand, sources, dests, forbidden, maximize, method]);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  // Redimensiona conservando los valores existentes.
  const resize = (newM: number, newN: number) => {
    const nm = Math.max(1, Math.min(8, newM));
    const nn = Math.max(1, Math.min(8, newN));
    setCosts((prev) =>
      Array.from({ length: nm }, (_, i) => Array.from({ length: nn }, (_, j) => prev[i]?.[j] ?? 0))
    );
    setForbidden((prev) =>
      Array.from({ length: nm }, (_, i) =>
        Array.from({ length: nn }, (_, j) => prev[i]?.[j] ?? false)
      )
    );
    setSupply((prev) => Array.from({ length: nm }, (_, i) => prev[i] ?? 0));
    setDemand((prev) => Array.from({ length: nn }, (_, j) => prev[j] ?? 0));
    setSources((prev) => Array.from({ length: nm }, (_, i) => prev[i] ?? `O${i + 1}`));
    setDests((prev) => Array.from({ length: nn }, (_, j) => prev[j] ?? `D${j + 1}`));
    setM(nm);
    setN(nn);
  };

  const setCost = (i: number, j: number, val: number) =>
    setCosts((prev) => prev.map((row, ri) => row.map((c, ci) => (ri === i && ci === j ? val : c))));

  const toggleForbidden = (i: number, j: number) =>
    setForbidden((prev) => prev.map((row, ri) => row.map((c, ci) => (ri === i && ci === j ? !c : c))));

  // Padea la matriz de costos originales a las dimensiones de un problema
  // balanceado (las celdas ficticias valen 0).
  const padOriginal = (orig: number[][], pm: number, pn: number) =>
    Array.from({ length: pm }, (_, i) =>
      Array.from({ length: pn }, (_, j) => orig[i]?.[j] ?? 0)
    );

  const validate = (): boolean => {
    setError(null);
    const flat = [...costs.flat(), ...(mode === 'transporte' ? [...supply, ...demand] : [])];
    if (flat.some((x) => Number.isNaN(x))) {
      setError('Hay celdas vacías o no numéricas. Completa todos los valores.');
      return false;
    }
    if (mode === 'transporte' && (supply.some((x) => x < 0) || demand.some((x) => x < 0))) {
      setError('La oferta y la demanda no pueden ser negativas.');
      return false;
    }
    return true;
  };

  const solveTransport = () => {
    if (!validate()) return;
    const raw: Problem = { costs, supply, demand, sources, dests };
    const eff = buildEffective(raw, maximize, forbidden);
    const { problem, note } = balanceProblem(eff.solveProblem);

    const init = method === 'noroeste' ? northwestCorner(problem) : minimumCost(problem);
    const opt = optimize(problem, init.alloc);

    const pm = problem.supply.length;
    const pn = problem.demand.length;
    const origPad = padOriginal(eff.originalCosts, pm, pn);

    setSolution({
      kind: 'transporte',
      problem,
      balanceNote: note,
      maximizeNote: eff.note,
      method,
      maximize,
      initial: init,
      optimal: opt,
      initialObjective: realObjective(init.alloc, origPad),
      optimalObjective: realObjective(opt.alloc, origPad),
    });
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
  };

  const solveAssign = () => {
    if (!validate()) return;
    const raw: Problem = { costs, supply, demand, sources, dests };
    const eff = buildEffective(raw, maximize, forbidden);
    const result = hungarian(eff.solveProblem.costs);

    // Objetivo real: ignora filas/columnas ficticias y rutas prohibidas.
    let objective = 0;
    for (const a of result.assignment) {
      if (a.row < result.origRows && a.col < result.origCols && !forbidden[a.row]?.[a.col]) {
        objective += eff.originalCosts[a.row][a.col];
      }
    }

    setSolution({
      kind: 'asignacion',
      result,
      rowLabels: sources,
      colLabels: dests,
      maximizeNote: eff.note,
      objective,
      maximize,
    });
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
  };

  const solve = () => (mode === 'transporte' ? solveTransport() : solveAssign());

  const loadExample = () => {
    setMode('transporte');
    resize(3, 3);
    setCosts(DEFAULT.costs.map((r) => r.slice()));
    setSupply(DEFAULT.supply.slice());
    setDemand(DEFAULT.demand.slice());
    setSources(DEFAULT.sources.slice());
    setDests(DEFAULT.dests.slice());
    setForbidden(DEFAULT.forbidden.map((r) => r.slice()));
    setMaximize(false);
    setSolution(null);
    setError(null);
  };

  const applyState = (s: SavedState) => {
    setMode(s.mode);
    setM(s.m);
    setN(s.n);
    setCosts(s.costs);
    setSupply(s.supply);
    setDemand(s.demand);
    setSources(s.sources);
    setDests(s.dests);
    setForbidden(s.forbidden);
    setMaximize(s.maximize);
    setMethod(s.method);
    setSolution(null);
  };

  const onShare = () => {
    const hash = encodeToHash(snapshot());
    const url = window.location.origin + window.location.pathname + hash;
    window.history.replaceState(null, '', hash);
    navigator.clipboard?.writeText(url).then(
      () => flash('¡Enlace copiado al portapapeles!'),
      () => flash('Enlace generado en la barra de direcciones.')
    );
  };

  const onLoadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        applyState(JSON.parse(reader.result as string));
        flash('Problema cargado.');
      } catch {
        setError('El archivo no es un problema válido.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const totalSupply = supply.reduce((a, b) => a + b, 0);
  const totalDemand = demand.reduce((a, b) => a + b, 0);
  const objLabel = maximize ? 'Utilidad' : 'Costo';

  return (
    <div className={`app ${theme}`}>
      <header className="hero screen-only">
        <div className="top-bar">
          <button className="ghost icon" onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}>
            {theme === 'dark' ? '☀️ Claro' : '🌙 Oscuro'}
          </button>
        </div>
        <h1>🚚 Calculadora de Problemas de Transporte</h1>
        <p>
          Esquina Noroeste · Costo Mínimo · optimización <b>MODI</b> · método <b>Húngaro</b> —
          con resolución paso a paso.
        </p>
      </header>

      <div className="tabs screen-only">
        <button className={mode === 'transporte' ? 'tab sel' : 'tab'} onClick={() => setMode('transporte')}>
          Problema de Transporte
        </button>
        <button className={mode === 'asignacion' ? 'tab sel' : 'tab'} onClick={() => setMode('asignacion')}>
          Problema de Asignación
        </button>
      </div>

      <section className="panel screen-only">
        <div className="dims">
          <label>
            {mode === 'transporte' ? 'Orígenes (filas)' : 'Trabajadores (filas)'}
            <input type="number" min={1} max={8} value={m} onChange={(e) => resize(Number(e.target.value), n)} />
          </label>
          <label>
            {mode === 'transporte' ? 'Destinos (columnas)' : 'Tareas (columnas)'}
            <input type="number" min={1} max={8} value={n} onChange={(e) => resize(m, Number(e.target.value))} />
          </label>

          <label className="check">
            <input type="checkbox" checked={maximize} onChange={(e) => setMaximize(e.target.checked)} />
            Maximizar
          </label>
          <button
            className={prohibitMode ? 'ghost on' : 'ghost'}
            onClick={() => setProhibitMode((p) => !p)}
            title="Activa el modo para marcar rutas/celdas prohibidas haciendo clic"
          >
            🚫 Prohibir {prohibitMode ? '(ON)' : ''}
          </button>
          <button className="ghost" onClick={loadExample}>
            Cargar ejemplo
          </button>
        </div>

        <div className="grid-wrap">
          <table className="t-input">
            <thead>
              <tr>
                <th className="corner" />
                {dests.map((d, j) => (
                  <th key={j}>
                    <input
                      className="lbl"
                      value={d}
                      onChange={(e) => setDests((p) => p.map((v, vi) => (vi === j ? e.target.value : v)))}
                    />
                  </th>
                ))}
                {mode === 'transporte' && <th className="supply-h">Oferta</th>}
              </tr>
            </thead>
            <tbody>
              {sources.map((s, i) => (
                <tr key={i}>
                  <th>
                    <input
                      className="lbl"
                      value={s}
                      onChange={(e) => setSources((p) => p.map((v, vi) => (vi === i ? e.target.value : v)))}
                    />
                  </th>
                  {dests.map((_, j) => (
                    <td key={j} className={forbidden[i]?.[j] ? 'forb' : ''}>
                      {forbidden[i]?.[j] ? (
                        <button className="forb-btn" onClick={() => toggleForbidden(i, j)} title="Quitar prohibición">
                          ✕
                        </button>
                      ) : prohibitMode ? (
                        <button className="forb-set" onClick={() => toggleForbidden(i, j)} title="Marcar como prohibida">
                          {Number.isNaN(costs[i][j]) ? '·' : costs[i][j]}
                        </button>
                      ) : (
                        <input
                          type="number"
                          value={Number.isNaN(costs[i][j]) ? '' : costs[i][j]}
                          onChange={(e) => setCost(i, j, parseFloat(e.target.value))}
                        />
                      )}
                    </td>
                  ))}
                  {mode === 'transporte' && (
                    <td className="edge">
                      <input
                        type="number"
                        value={Number.isNaN(supply[i]) ? '' : supply[i]}
                        onChange={(e) =>
                          setSupply((p) => p.map((v, vi) => (vi === i ? parseFloat(e.target.value) : v)))
                        }
                      />
                    </td>
                  )}
                </tr>
              ))}
              {mode === 'transporte' && (
                <tr>
                  <th>Demanda</th>
                  {dests.map((_, j) => (
                    <td key={j} className="edge">
                      <input
                        type="number"
                        value={Number.isNaN(demand[j]) ? '' : demand[j]}
                        onChange={(e) =>
                          setDemand((p) => p.map((v, vi) => (vi === j ? parseFloat(e.target.value) : v)))
                        }
                      />
                    </td>
                  ))}
                  <td className="corner" />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {mode === 'transporte' && (
          <div className={`balance ${totalSupply === totalDemand ? 'ok' : 'warn'}`}>
            Oferta total = {totalSupply} · Demanda total = {totalDemand}
            {totalSupply === totalDemand
              ? ' — problema balanceado ✔'
              : ' — se agregará una fila/columna ficticia ⚠'}
          </div>
        )}

        <div className="method-row">
          {mode === 'transporte' && (
            <>
              <span>Método inicial:</span>
              <label className={method === 'noroeste' ? 'sel' : ''}>
                <input type="radio" checked={method === 'noroeste'} onChange={() => setMethod('noroeste')} />
                Esquina Noroeste
              </label>
              <label className={method === 'costo' ? 'sel' : ''}>
                <input type="radio" checked={method === 'costo'} onChange={() => setMethod('costo')} />
                Costo Mínimo
              </label>
            </>
          )}
          <button className="solve" onClick={solve}>
            Resolver ▶
          </button>
        </div>

        <div className="io-row">
          <button className="ghost" onClick={() => downloadJson(snapshot())}>
            💾 Guardar
          </button>
          <label className="ghost file-label">
            📂 Cargar
            <input type="file" accept="application/json" onChange={onLoadFile} hidden />
          </label>
          <button className="ghost" onClick={onShare}>
            🔗 Compartir
          </button>
          {solution && (
            <button className="ghost" onClick={() => window.print()}>
              🖨️ Exportar PDF
            </button>
          )}
        </div>

        {error && <div className="error">{error}</div>}
      </section>

      {toast && <div className="toast screen-only">{toast}</div>}

      {/* ---------- Resultados ---------- */}
      <div ref={resultRef} />

      {solution?.kind === 'transporte' && (
        <>
          <section className="results screen-only">
            {solution.maximizeNote && <div className="note">{solution.maximizeNote}</div>}
            {solution.balanceNote && <div className="note">{solution.balanceNote}</div>}

            <div className="viewers">
              <StepViewer
                title={`Solución inicial — ${
                  solution.method === 'noroeste' ? 'Esquina Noroeste' : 'Costo Mínimo'
                }`}
                problem={solution.problem}
                steps={solution.initial.steps}
                totalCost={solution.initialObjective}
                accent="#2563eb"
              />
              <StepViewer
                title="Optimización — Método MODI"
                problem={solution.problem}
                steps={solution.optimal.steps}
                totalCost={solution.optimalObjective}
                accent="#059669"
              />
            </div>

            <h3 className="diagram-title">Diagrama de flujo de la solución óptima</h3>
            <FlowDiagram problem={solution.problem} alloc={solution.optimal.alloc} />

            <div className="summary">
              <div className="sum-card">
                <span className="sum-label">{objLabel} inicial</span>
                <span className="sum-val">{solution.initialObjective}</span>
              </div>
              <div className="sum-card hi">
                <span className="sum-label">{objLabel} óptimo</span>
                <span className="sum-val">{solution.optimalObjective}</span>
              </div>
              <div className="sum-card">
                <span className="sum-label">{maximize ? 'Mejora' : 'Ahorro'}</span>
                <span className="sum-val">
                  {Math.abs(solution.optimalObjective - solution.initialObjective)}
                </span>
              </div>
              <div className="sum-card">
                <span className="sum-label">Iteraciones MODI</span>
                <span className="sum-val">{solution.optimal.iterations}</span>
              </div>
            </div>

            <div className="legend">
              <span>
                <i className="lg basic" /> Celda básica
              </span>
              <span>
                <i className="lg plus" /> Recibe (+)
              </span>
              <span>
                <i className="lg minus" /> Cede (−)
              </span>
              <span>
                <i className="lg neg" /> Costo reducido negativo
              </span>
            </div>
          </section>

          <PrintReport
            problem={solution.problem}
            methodName={solution.method === 'noroeste' ? 'Esquina Noroeste' : 'Costo Mínimo'}
            balanceNote={solution.balanceNote}
            maximizeNote={solution.maximizeNote}
            initial={solution.initial}
            optimal={solution.optimal}
            objectiveLabel={objLabel}
            initialObjective={solution.initialObjective}
            optimalObjective={solution.optimalObjective}
          />
        </>
      )}

      {solution?.kind === 'asignacion' && (
        <section className="results">
          {solution.maximizeNote && <div className="note screen-only">{solution.maximizeNote}</div>}
          <div className="viewers single">
            <HungarianViewer
              steps={solution.result.steps}
              rowLabels={solution.rowLabels}
              colLabels={solution.colLabels}
              origRows={solution.result.origRows}
              origCols={solution.result.origCols}
            />
          </div>
          <div className="summary">
            <div className="sum-card hi">
              <span className="sum-label">{objLabel} óptimo</span>
              <span className="sum-val">{solution.objective}</span>
            </div>
            <div className="sum-card wide">
              <span className="sum-label">Asignaciones</span>
              <span className="sum-val sm">
                {solution.result.assignment
                  .filter((a) => a.row < solution.result.origRows && a.col < solution.result.origCols)
                  .map((a) => `${solution.rowLabels[a.row]}→${solution.colLabels[a.col]}`)
                  .join('  ·  ')}
              </span>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default App;
